import React from 'react'
import {cx} from '@emotion/css'
import styled from '@emotion/styled'
import {useDefaultLayout} from 'hooks/useDefaultLayout'
import {AnimatePresence, motion} from 'motion/react'

import {Button, buttonSizesMap} from '@vio/atlas/Button'
import {Icon} from '@vio/atlas/Icon'
import {cssTheme} from '@vio/atlas/themes'
import {linariaMq} from '@vio/atlas/utils/breakpoints'
import minus from '@vio/atlas/utils/minus'

import {StickyWrapper} from './styles'
import useBackToTop from './useBackToTop'

interface Props {
  onClick: () => void
  enabled: boolean
  verticalOffset?: number
}

const buttonSize = buttonSizesMap.lg.size
// We'll float it outside when there is enough space to fit + space up to the window edge
const {maxPageWidth} = cssTheme.layout // match PageWidthContainer
const spaceFromContent = cssTheme.layout.spacing.s400
const spaceFromEdge = cssTheme.layout.spacing.s400
const minSpace = `calc(${spaceFromContent} + ${buttonSize}px + ${spaceFromEdge}) * 2`
const floatOutSideBreakpoint = `calc(${maxPageWidth} + ${minSpace})`

const ButtonsWrapper = styled.div`
  position: absolute;
  display: inline-block;
  right: ${cssTheme.layout.spacing.s300}; /* match offer padding */
  bottom: ${cssTheme.layout.spacing.s600};
  ${linariaMq.desktopXs} {
    /* match ExpandableHotelCard > CardContent padding */
    right: ${cssTheme.layout.spacing.s500};
    bottom: ${cssTheme.layout.spacing.s700};
  }
  &.--not-map-visible {
    @media (min-width: ${floatOutSideBreakpoint}) {
      right: ${minus(`(${spaceFromContent} + ${buttonSize})`)};
    }
  }
`

export const BackToTopButton = ({onClick, enabled = true}: Props) => {
  const showBackToTop = useBackToTop(enabled)
  const {isMapVisible} = useDefaultLayout()

  return (
    <StickyWrapper>
      <AnimatePresence>
        {showBackToTop && (
          <motion.div
            key="backToTop"
            initial={{opacity: 0, y: 100}}
            animate={{opacity: 1, y: 0}}
            exit={{opacity: 0, y: 100}}
            transition={{
              duration: 0.15
            }}
          >
            <ButtonsWrapper
              className={cx(!isMapVisible && '--not-map-visible')}
            >
              <Button
                isRounded
                isFloating
                variant="tertiary"
                onClick={onClick}
                iconStart={<Icon name="ArrowUp" />}
              />
            </ButtonsWrapper>
          </motion.div>
        )}
      </AnimatePresence>
    </StickyWrapper>
  )
}
